import { useState } from 'react';
import { useAuthContext } from '../../auth/AuthContext';
import { createScsObjectivesPdf } from '../../utils/scs-objectives-pdf';

type Props = {
  answers: Record<string, unknown>;
  formId?: string;
  formVersion?: number;
};

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ScsObjectivesDownload = ({ answers, formId, formVersion }: Props) => {
  const { fetchJWT } = useAuthContext();
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');

  const downloadObjectives = async () => {
    setError('');
    setIsDownloading(true);

    try {
      const token = (await fetchJWT()).idToken;
      if (!token) throw new Error('No auth token');

      const { blob, filename } = await createScsObjectivesPdf(token, answers, formVersion, formId);
      triggerDownload(blob, filename);
    } catch (downloadError) {
      console.error('SCS objectives PDF download failed', downloadError);
      setError('Unable to download your objectives right now. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="govuk-!-margin-bottom-6">
      <button
        className="govuk-button"
        type="button"
        data-module="govuk-button"
        data-prevent-double-click="true"
        disabled={isDownloading}
        onClick={() => void downloadObjectives()}
      >
        {isDownloading ? 'Downloading...' : 'Download my objectives'}
      </button>
      {error && (
        <p className="govuk-error-message" role="alert">
          {error}
        </p>
      )}
    </div>
  );
};

export default ScsObjectivesDownload;
